export enum AscCrosswordPuzzleBoardSize {
  SIX_BY_SIX = 6,
  EIGHT_BY_EIGHT = 8,
  TEN_BY_TEN = 10,
  TWELVE_BY_TWELVE = 12,
}

export interface AscCrosswordPuzzleWordScope {
  beginningCellNumber: number;
  length?: number;
  /**
   *  wordIndex is used for solve mode only
   * @property number
   */
  wordIndex?: number;
  /**
   *  word is used for create mode only
   * @property string
   */
  word?: string;
  id?:string;
}

export type AscCrosswordPuzzleWordCell = {
  acrossWordIndex?: number;
  downWordIndex?: number;
  id: number;
  row: number;
  column: number;
  isFirstCellOfWord?: boolean;
  error?: boolean;
};
export interface AscCrosswordPuzzleBoardProps {
  className?: string;
  /**
   * Defines how the puzzle board will behave.
   *
   * Create mode:
   * - Will generate word indexes automatically if they are not provided.
   * - Is read-only, for visualization purposes.
   * - Accepts an additional word property for wordsAcross and wordsDown.
   *
   * Solve mode:
   * - Accepts an additional wordIndex property for wordsAcross and wordsDown.
   * - Will not generate word indexes, will use provided wordIndexes.
   * - Is solvable.
   *
   * @property "create" | "solve" | "completed"
   * @default "solve"
   */
  mode?: 'create' | 'solve' | 'completed';
  /**
   * Size of the puzzle board.
   * @property AscCrosswordPuzzleBoardSize
   * @default AscCrosswordPuzzleBoardSize.TEN_BY_TEN
   */
  size?: AscCrosswordPuzzleBoardSize;
  /**
   * For words that are across.
   */
  wordsAcross: AscCrosswordPuzzleWordScope[];
  /**
   * For words that are down.
   */
  wordsDown: AscCrosswordPuzzleWordScope[];
  onChange?: (words: AscCrosswordPuzzleWordResponse) => void;
  onError?: (error: boolean) => void;
  onGenerate?: (
    words: AscCrosswordPuzzleWordResponse<
      Required<AscCrosswordPuzzleWordScope>
    >,
    error?: boolean
  ) => void;
  disabled?: boolean;
}

interface AscCrosswordPuzzleBasicProps {
  className?: string;
  mode?: 'solve' | 'create' | 'completed';
  size?: AscCrosswordPuzzleBoardSize;
  onError?: (error: boolean) => void;
  disabled?: boolean;
}

interface AscCrosswordPuzzleCreateProps extends AscCrosswordPuzzleBasicProps {
  wordsAcross: AscCrosswordPuzzleWordCreate[];
  wordsDown: AscCrosswordPuzzleWordCreate[];
  mode: 'create';
  onGenerate?: (
    words: AscCrosswordPuzzleWordResponse<
      Required<AscCrosswordPuzzleWordScope>
    >,
    size: AscCrosswordPuzzleBoardSize
  ) => void;
  onSolve?: never;
}

interface AscCrosswordPuzzleSolveProps extends AscCrosswordPuzzleBasicProps {
  wordsAcross: AscCrosswordPuzzleWordSolve[];
  wordsDown: AscCrosswordPuzzleWordSolve[];
  mode: 'solve';
  onGenerate?: never;
  onSolve?: (words: AscCrosswordPuzzleWordResponse) => void;
}

interface AscCrosswordPuzzleCompletedProps
  extends AscCrosswordPuzzleBasicProps {
  wordsAcross: AscCrosswordPuzzleWordCompleted[];
  wordsDown: AscCrosswordPuzzleWordCompleted[];
  mode: 'completed';
  onGenerate?: never;
  onSolve?: never;
}

export type AscCrosswordPuzzleProps =
  | AscCrosswordPuzzleCreateProps
  | AscCrosswordPuzzleSolveProps
  | AscCrosswordPuzzleCompletedProps;

export interface AscCrosswordPuzzleWordCompleted {
  wordIndex: number;
  beginningCellNumber: number;
  length: number;
  clue: string;
  word?: string;
}
export interface AscCrosswordPuzzleWordSolve {
  wordIndex: number;
  beginningCellNumber: number;
  length: number;
  clue: string;
}

export interface AscCrosswordPuzzleWordCreate {
  wordIndex?: number;
  word: string;
  beginningCellNumber: number;
  clue: string;
}

export interface AscCrosswordPuzzleWord {
  wordIndex: number;
  word: string;
}

export interface AscCrosswordPuzzleWordResponse<
  T extends NonNullable<any> = AscCrosswordPuzzleWord
> {
  wordsAcross: T[];
  wordsDown: T[];
}
